import * as React from 'react';
import { Pressable, type PressableProps } from './pressable';

/**
 * Chip visuals from the design system (Figma "Input/Food-Types").
 *
 * - unselected: white fill, 1px Cloud border, Evergreen label
 * - selected:   Seafoam fill, no border, white label
 *
 * Exported as a plain style object because the chip appears in two shapes:
 * `Chip` below (interactive, renders a button) and `Pill`
 * (`components/OnboardingForm/Pill`), which is presentational because its
 * call sites already wrap it in a `Pressable`. Both read from here so the
 * two cannot drift apart.
 */
export const chipStyles = (isSelected: boolean) =>
  ({
    px: '15px',
    py: '8px',
    rounded: 'pill',
    borderWidth: '1.5px',
    borderColor: isSelected ? 'secondary.200' : 'cloud.100',
    bg: isSelected ? 'secondary.200' : 'white',
    color: isSelected ? 'white' : 'secondary.500',
    fontFamily: 'body',
    fontSize: '13.5px',
    fontWeight: isSelected ? 600 : 500,
    lineHeight: '1.25',
    letterSpacing: '0.01em',
    // Selected chips are already Seafoam, so nothing shifts on hover.
    _hover: { borderColor: 'secondary.200' },
  }) as const;

/**
 * Project Chip: the interactive form of the chip above. Content sized, so it
 * flows inside a wrapping row with `gap={2}` (8px in the design).
 */
export type ChipProps = Omit<PressableProps, 'children'> & {
  isSelected?: boolean;
  children: React.ReactNode;
};

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ isSelected = false, children, ...rest }, ref) => (
    <Pressable
      ref={ref}
      aria-pressed={isSelected}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      {...chipStyles(isSelected)}
      {...rest}
    >
      {children}
    </Pressable>
  )
);
Chip.displayName = 'Chip';
